import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Rocket,
  TrendingUp,
  Target,
  Bot,
  Zap,
  History,
  Cpu,
  Radar,
  Wand2,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";

const NEON = "#F5C542";
const BLUE2 = "#F28C28";
const BG = "#120D0D";
const CARD = "#1A1A1A";

const LEVELS = ["Excelente", "Muito Bom", "Bom", "Aguardando"];
const LEVEL_COLORS = {
  Excelente: { fg: "#00E0A4", bg: "#00E0A422", bd: "#00E0A455" },
  "Muito Bom": { fg: NEON, bg: `${NEON}20`, bd: `${NEON}55` },
  Bom: { fg: BLUE2, bg: `${BLUE2}22`, bd: `${BLUE2}55` },
  Aguardando: { fg: "#c9c9c9", bg: "#ffffff10", bd: "#ffffff30" },
};

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const randFloat = (min, max, decimals = 2) => {
  const v = Math.random() * (max - min) + min;
  return Number(v.toFixed(decimals));
};

const genMultipliers = () =>
  Array.from({ length: 15 }, () => {
    // weighted: some tiny (1.0-1.5), some medium (1.5-4), some big (4-20)
    const r = Math.random();
    if (r < 0.35) return randFloat(1.0, 1.5);
    if (r < 0.75) return randFloat(1.5, 5);
    if (r < 0.94) return randFloat(5, 12);
    return randFloat(12, 30);
  });

const genSignal = () => ({
  id: Date.now() + Math.random(),
  rounds: randInt(2, 12),
  target: randFloat(1.8, 15, 2),
  attempts: randInt(1, 6),
  level: LEVELS[Math.floor(Math.random() * LEVELS.length)],
  time: new Date(),
});

const LOADING_STEPS = [
  { icon: Radar, label: "Analisando histórico..." },
  { icon: Cpu, label: "Processando algoritmo..." },
  { icon: Wand2, label: "Gerando estratégia..." },
];

const COOLDOWN_SECONDS = 30;

const CrashParticles = () => {
  const dots = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        s: Math.random() * 2 + 1,
        d: Math.random() * 6 + 5,
        delay: Math.random() * 5,
      })),
    []
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {dots.map((p) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: [0, 0.7, 0], y: [0, -60, -120] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.delay, ease: "linear" }}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.s,
            height: p.s,
            background: NEON,
            boxShadow: `0 0 10px ${NEON}`,
          }}
        />
      ))}
    </div>
  );
};

const MultiplierPill = ({ value }) => {
  const high = value >= 2;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="shrink-0 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-mono font-bold border tabular-nums"
      style={
        high
          ? {
              color: NEON,
              background: `${NEON}15`,
              borderColor: `${NEON}55`,
              boxShadow: `0 0 14px ${NEON}30`,
            }
          : {
              color: "#a1a1aa",
              background: "#ffffff08",
              borderColor: "#ffffff18",
            }
      }
    >
      {value.toFixed(2)}x
    </motion.div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, delay, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -4 }}
    className="relative rounded-2xl p-5 overflow-hidden"
    style={{
      background: `linear-gradient(160deg, ${CARD}, #101010)`,
      border: `1px solid ${NEON}25`,
      boxShadow: `0 0 30px ${NEON}0d, inset 0 0 40px rgba(245, 197, 66, 0.02)`,
    }}
    data-testid={testid}
  >
    <div
      aria-hidden
      className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-30 pointer-events-none"
      style={{ background: `radial-gradient(circle, ${NEON}55, transparent 65%)` }}
    />
    <div className="flex items-center justify-between relative">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{
          background: `${NEON}15`,
          border: `1px solid ${NEON}40`,
        }}
      >
        <Icon className="w-5 h-5" style={{ color: NEON }} />
      </div>
    </div>
    <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-white/50 font-medium relative">
      {label}
    </div>
    <div
      className="mt-1.5 font-display font-black text-2xl md:text-3xl tracking-tight leading-tight relative"
      style={{ color: "#fff" }}
    >
      {value}
    </div>
    {sub && <div className="mt-2 text-[11px] text-white/40 relative">{sub}</div>}
  </motion.div>
);

const LevelBadge = ({ level }) => {
  const c = LEVEL_COLORS[level] || LEVEL_COLORS.Bom;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide"
      style={{ color: c.fg, background: c.bg, border: `1px solid ${c.bd}` }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: c.fg, boxShadow: `0 0 8px ${c.fg}` }}
      />
      {level}
    </span>
  );
};

export default function CrashSignals() {
  const [multipliers, setMultipliers] = useState(genMultipliers);
  const [current, setCurrent] = useState(genSignal);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const cdRef = useRef(null);

  useEffect(() => {
    document.title = "Gerador de Sinais Crash — Manga";
    document.body.style.background = BG;
    return () => {
      document.body.style.background = "";
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) {
      if (cdRef.current) clearInterval(cdRef.current);
      return;
    }
    cdRef.current = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(cdRef.current);
  }, [cooldown]);

  const generate = async () => {
    if (loading || cooldown > 0) return;
    setLoading(true);
    setLoadStep(0);
    // 3 steps over ~2s
    await new Promise((r) => setTimeout(r, 700));
    setLoadStep(1);
    await new Promise((r) => setTimeout(r, 700));
    setLoadStep(2);
    await new Promise((r) => setTimeout(r, 600));

    const next = genSignal();
    setHistory((h) => [current, ...h].slice(0, 10));
    setCurrent(next);
    setMultipliers(genMultipliers());
    setLoading(false);
    setCooldown(COOLDOWN_SECONDS);
  };

  const btnDisabled = loading || cooldown > 0;

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden"
      style={{
        background: `radial-gradient(1200px 700px at 50% -10%, ${NEON}12, transparent 60%), radial-gradient(900px 500px at 90% 30%, ${BLUE2}12, transparent 60%), ${BG}`,
      }}
      data-testid="crash-page"
    >
      <CrashParticles />

      {/* Header nav */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 pt-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          data-testid="crash-back"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar ao início
        </Link>
        <div className="flex items-center gap-2">
          <img src="/manga-symbol.png" alt="" className="w-8 h-8 object-contain" />
          <div className="font-display font-black text-sm tracking-tight">
            GRUPO <span style={{ color: NEON }}>MANGA</span>
          </div>
        </div>
      </header>

      {/* Banner */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 pt-10 md:pt-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden p-6 md:p-10"
          style={{
            background: `linear-gradient(140deg, #0d1626, #05101d 55%, #0a0a0a)`,
            border: `1px solid ${NEON}33`,
            boxShadow: `0 0 60px ${NEON}18`,
          }}
        >
          {/* Rocket trail lines */}
          <svg
            className="absolute inset-0 w-full h-full opacity-40 pointer-events-none"
            viewBox="0 0 800 300"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id="crashTrail" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0" stopColor={NEON} stopOpacity="0" />
                <stop offset="1" stopColor={NEON} stopOpacity="0.9" />
              </linearGradient>
            </defs>
            <path
              d="M 0 280 Q 250 260 400 180 T 800 20"
              stroke="url(#crashTrail)"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M 0 300 Q 300 280 500 200 T 800 60"
              stroke={`${BLUE2}66`}
              strokeWidth="1"
              fill="none"
            />
          </svg>

          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <h1
                className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl md:text-5xl leading-[0.95]"
                data-testid="crash-title"
              >
                Gerador de <br className="hidden sm:block" />
                Sinais{" "}
                <span
                  style={{
                    background: `linear-gradient(90deg, ${NEON}, ${BLUE2})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Crash
                </span>
              </h1>
              <p className="mt-3 text-white/60 text-sm md:text-base max-w-2xl leading-relaxed">
                Gere estratégias automaticamente utilizando um algoritmo inteligente.
                Parâmetros como rodada de entrada, multiplicador sugerido e tentativas
                organizados em uma interface moderna e intuitiva.
              </p>
            </div>

            {/* Rocket emblem */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="relative shrink-0 hidden md:flex items-center justify-center"
            >
              <div
                className="w-24 h-24 rounded-2xl flex items-center justify-center"
                style={{
                  background: `radial-gradient(circle, ${NEON}25, ${NEON}05)`,
                  border: `1px solid ${NEON}55`,
                  boxShadow: `0 0 60px ${NEON}55`,
                }}
              >
                <Rocket className="w-12 h-12" style={{ color: NEON }} />
              </div>
            </motion.div>
          </div>

          {/* Multiplier history strip */}
          <div className="relative mt-6">
            <div className="flex items-center gap-2 mb-2">
              <History className="w-3.5 h-3.5" style={{ color: NEON }} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/45 font-bold">
                Últimos multiplicadores
              </span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="crash-multipliers">
              {multipliers.map((m, i) => (
                <MultiplierPill key={`${m}-${i}`} value={m} />
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Cards */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          <StatCard
            testid="card-rounds"
            icon={Rocket}
            label="Entrar Após"
            value={`Após ${current.rounds} ${current.rounds === 1 ? "Rodada" : "Rodadas"}`}
            sub="Aleatório entre 2 e 12"
            delay={0.05}
          />
          <StatCard
            testid="card-target"
            icon={TrendingUp}
            label="Buscar Multiplicador"
            value={`${current.target.toFixed(2)}x`}
            sub="Entre 1.80x e 15.00x"
            delay={0.12}
          />
          <StatCard
            testid="card-attempts"
            icon={Target}
            label="Tentativas"
            value={`${current.attempts} ${current.attempts === 1 ? "Tentativa" : "Tentativas"}`}
            sub="Entre 1 e 6"
            delay={0.19}
          />
          {/* Level card — inline styling to show badge */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.26, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ y: -4 }}
            className="relative rounded-2xl p-5 overflow-hidden"
            style={{
              background: `linear-gradient(160deg, ${CARD}, #101010)`,
              border: `1px solid ${NEON}25`,
              boxShadow: `0 0 30px ${NEON}0d`,
            }}
            data-testid="card-level"
          >
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-56 h-56 rounded-full opacity-30 pointer-events-none"
              style={{ background: `radial-gradient(circle, ${NEON}55, transparent 65%)` }}
            />
            <div className="flex items-center justify-between relative">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${NEON}15`, border: `1px solid ${NEON}40` }}
              >
                <Bot className="w-5 h-5" style={{ color: NEON }} />
              </div>
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-[0.18em] text-white/50 font-medium relative">
              Nível do Sinal
            </div>
            <div className="mt-2 relative">
              <LevelBadge level={current.level} />
            </div>
            <div className="mt-2 text-[11px] text-white/40 relative">
              Excelente • Muito Bom • Bom • Aguardando
            </div>
          </motion.div>
        </div>

        {/* Generate button + cooldown */}
        <div className="mt-10 flex flex-col items-center gap-4">
          <motion.button
            onClick={generate}
            disabled={btnDisabled}
            whileTap={{ scale: 0.96 }}
            className="relative overflow-hidden rounded-full px-10 py-5 font-display font-black uppercase tracking-wider text-white text-sm md:text-base disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${BLUE2}, ${NEON})`,
              boxShadow: `0 10px 40px ${NEON}55, inset 0 0 0 1px rgba(255,255,255,0.15)`,
            }}
            data-testid="crash-generate-btn"
          >
            {!btnDisabled && (
              <motion.span
                aria-hidden
                className="absolute inset-0"
                animate={{ opacity: [0.35, 0.65, 0.35] }}
                transition={{ duration: 1.6, repeat: Infinity }}
                style={{
                  boxShadow: `0 0 40px ${NEON}, 0 0 80px ${NEON}80`,
                  borderRadius: 9999,
                }}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-2">
              <Zap className="w-4 h-4" /> {loading ? "Gerando..." : "GERAR NOVO SINAL"}
            </span>
          </motion.button>

          {cooldown > 0 && !loading && (
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-1">
                Novo sinal disponível em
              </div>
              <div
                className="font-mono font-black text-4xl tabular-nums"
                style={{ color: NEON, textShadow: `0 0 20px ${NEON}80` }}
                data-testid="crash-cooldown"
              >
                {cooldown}
              </div>
              <div className="mt-2 w-40 mx-auto h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full"
                  style={{ background: `linear-gradient(90deg, ${NEON}, ${BLUE2})` }}
                  initial={false}
                  animate={{ width: `${(cooldown / COOLDOWN_SECONDS) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* History table */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 mt-14 pb-16">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4" style={{ color: NEON }} />
          <h3 className="font-display font-bold text-white text-lg uppercase tracking-wide">
            Últimos Sinais
          </h3>
        </div>
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: `linear-gradient(160deg, ${CARD}, #0e0e0e)`,
            border: `1px solid ${NEON}22`,
          }}
          data-testid="crash-history"
        >
          {history.length === 0 ? (
            <div className="p-6 text-center text-white/40 text-sm">
              Nenhum sinal ainda. Gere o primeiro para popular o histórico.
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {history.map((h, i) => (
                <motion.div
                  key={h.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.03 }}
                  className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 hover:bg-white/[0.02] transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-mono font-bold text-xs"
                    style={{
                      background: `${NEON}10`,
                      color: NEON,
                      border: `1px solid ${NEON}30`,
                    }}
                  >
                    #{history.length - i}
                  </div>
                  <div className="flex-1 min-w-0 text-sm text-white/80">
                    <span className="font-semibold">Após {h.rounds} {h.rounds === 1 ? "Rodada" : "Rodadas"}</span>
                    <span className="text-white/30 mx-1.5">•</span>
                    <span>Buscar <span style={{ color: NEON }}>{h.target.toFixed(2)}x</span></span>
                    <span className="text-white/30 mx-1.5">•</span>
                    <span>{h.attempts} {h.attempts === 1 ? "Tentativa" : "Tentativas"}</span>
                  </div>
                  <div className="hidden sm:block">
                    <LevelBadge level={h.level} />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.06] py-8 text-center text-white/40 text-xs">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="font-display font-bold text-white/70 mb-1 tracking-wide">
            © 2026 GRUPO MANGA
          </div>
          <div>Gerador Inteligente de Sinais</div>
          <div>Interface Premium</div>
        </div>
      </footer>

      {/* Loading overlay */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
              background: "rgba(6,10,20,0.75)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
            data-testid="crash-loading"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-2xl p-8 max-w-sm w-[92%]"
              style={{
                background: `linear-gradient(160deg, ${CARD}, #0a0a0a)`,
                border: `1px solid ${NEON}55`,
                boxShadow: `0 0 60px ${NEON}55`,
              }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: `${NEON}18`,
                      border: `1px solid ${NEON}55`,
                    }}
                  >
                    {(() => {
                      const Icon = LOADING_STEPS[loadStep].icon;
                      return <Icon className="w-8 h-8" style={{ color: NEON }} />;
                    })()}
                  </div>
                  <motion.div
                    className="absolute inset-0 rounded-full border-2"
                    style={{ borderColor: `${NEON}55`, borderTopColor: NEON }}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                  />
                </div>
                <div className="mt-5 space-y-2 w-full">
                  {LOADING_STEPS.map((s, i) => {
                    const done = i < loadStep;
                    const active = i === loadStep;
                    return (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-sm transition-opacity duration-300"
                        style={{
                          color: active ? "#fff" : done ? "#8a8a8a" : "#555",
                          opacity: active ? 1 : done ? 0.7 : 0.4,
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{
                            background: active || done ? NEON : "#555",
                            boxShadow: active ? `0 0 8px ${NEON}` : "none",
                          }}
                        />
                        {s.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
